import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AiFillBug } from 'react-icons/ai';
import { Button, Flex } from 'core/components';
import MitigateAlertButton from 'app/views/alerting/components/alertActions/MitigateAlertButton';
import AckAlertButton from 'app/views/alerting/components/alertActions/AckAlertButton';
import UnackAlertButton from 'app/views/alerting/components/alertActions/UnackAlertButton';
import SilenceAlertButton from 'app/views/alerting/components/alertActions/SilenceAlertButton';
import SuppressAlertButton from 'app/views/alerting/components/alertActions/SuppressAlertButton';
import ClearAlertButton from 'app/views/alerting/components/alertActions/ClearAlertButton';
import DashboardLink from 'app/components/links/DashboardLink';
import PolicyLink from 'app/components/links/PolicyLink';
import AlertDebugDialog from 'app/views/alerting/components/alertDebugger/AlertDebugDialog';
import AlertAiAdvisorButton from 'app/views/alerting/components/alertActions/AlertAiAdvisorButton';

/**
 * This component displays various actions that can be take on an alert:
 * - View Details
 * - AI Investigation
 * - Ack/unack
 * - Clear
 * - Silence
 * - Suppress
 * - Mitigate
 * - Dashboard link
 * - Policy link
 * - Debug
 */
const AlertActions = ({ alertModel = {}, isSubtenant, tenant, showPolicyLink, showAiAdvisor, showDetailsLink }) => {
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const { policy } = alertModel;
  const dashboardId = policy?.get('dashboardID');
  const policyId = policy?.id;

  const debugAlertButton = React.useRef(null);

  const toggleDebugDialogVisibility = () => {
    setShowDebugDialog(!showDebugDialog);
    if (showDebugDialog && debugAlertButton.current.buttonRef) {
      debugAlertButton.current.buttonRef.focus();
    }
  };

  return (
    <Flex flexDirection="column" gap="2px">
      {showDetailsLink && (
        <Button
          textAlign="left"
          icon="zoom-in"
          rightIcon="arrow-right"
          fill
          minimal
          onClick={() => window.open(alertModel.alertDetailLink, '_blank')}
        >
          View Alert Details
        </Button>
      )}
      {!isSubtenant && (
        <>
          {showAiAdvisor && (
            <AlertAiAdvisorButton
              policy={policy}
              alertModel={alertModel}
              isSubtenant={isSubtenant}
              buttonProps={{ fill: true, minimal: true }}
            />
          )}
          {!alertModel.isAcked && <AckAlertButton model={alertModel} buttonProps={{ fill: true, minimal: true }} />}
          {alertModel.isAcked && <UnackAlertButton model={alertModel} buttonProps={{ fill: true, minimal: true }} />}
          {alertModel.isActive && <ClearAlertButton models={alertModel} buttonProps={{ fill: true, minimal: true }} />}
          <SilenceAlertButton alertModel={alertModel} buttonProps={{ fill: true, minimal: true }} />
          <SuppressAlertButton alertModel={alertModel} buttonProps={{ fill: true, minimal: true }} />
          {alertModel.canMitigate && (
            <MitigateAlertButton alertModel={alertModel} tenant={tenant} buttonProps={{ fill: true, minimal: true }} />
          )}
        </>
      )}

      {!isSubtenant && !!policyId && showPolicyLink && (
        <PolicyLink
          model={policy}
          id={policyId}
          as="Button"
          fill
          icon="edit"
          alignText="left"
          rightIcon="arrow-right"
          minimal
        >
          {tenant ? 'Edit Parent Policy' : 'Edit Policy'}
        </PolicyLink>
      )}
      {dashboardId && alertModel.supportsDashboards && (
        <DashboardLink
          as="Button"
          fill
          withIcon
          rightIcon="arrow-right"
          id={dashboardId}
          query={alertModel.dashboardQuery}
          alignText="left"
          minimal
        >
          View Dashboard
        </DashboardLink>
      )}

      {!isSubtenant && (
        <Button
          fill
          textAlign="left"
          onClick={toggleDebugDialogVisibility}
          onSpaceOrEnter={toggleDebugDialogVisibility}
          icon={AiFillBug}
          ariaExpanded={showDebugDialog}
          ref={debugAlertButton}
          minimal
        >
          Debug Alert&hellip;
        </Button>
      )}

      {!!showDebugDialog && (
        <AlertDebugDialog isOpen={showDebugDialog} alertModel={alertModel} onClose={toggleDebugDialogVisibility} />
      )}
    </Flex>
  );
};

AlertActions.propTypes = {
  // This must be an AlertModel instance
  alertModel: PropTypes.object.isRequired,
  isSubtenant: PropTypes.bool,
  // TenantModel
  tenant: PropTypes.object,
  showPolicyLink: PropTypes.bool,
  showAiAdvisor: PropTypes.bool,
  showDetailsLink: PropTypes.bool
};

AlertActions.defaultProps = {
  isSubtenant: undefined,
  tenant: undefined,
  showPolicyLink: undefined,
  showAiAdvisor: true,
  showDetailsLink: true
};

export default AlertActions;
